import type { ComponentProps } from "react";
import { forwardRef } from "react";
import { Modal as UIModal } from "ancestry-ui-react";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";

/**
 * Prop types mirror the real `ancestry-ui-react` Modal by deriving them directly
 * from it, so the wrapper stays a drop-in replacement and cannot drift from
 * `desc.d.ts`.
 */
export type ModalProps = ComponentProps<typeof UIModal>;

/**
 * Safe boundary wrapper for `ancestry-ui-react`'s `Modal` (ADR-012 §3,
 * gate-the-mount).
 *
 * The underlying `Modal` reads `window.ui.modal` unconditionally in
 * `componentDidMount`, so mounting it before Ancestry's vanilla UI library
 * (`core.js`) has populated `window.ui` throws and surfaces as a `Main` FCI. We gate
 * the mount on the shared, hydration-safe readiness signal: render nothing until
 * `window.ui.modal` exists, then mount the real component.
 *
 * A closed modal paints no visible DOM, so gating introduces no layout shift; the
 * trigger element lives at the call site (outside this wrapper) and still renders.
 *
 * The real `Modal` accepts a `ref`, so we forward it to stay a faithful drop-in.
 */
export const Modal = forwardRef<unknown, ModalProps>((props, ref) => {
    const isLoaded = useUIComponentLoaded("modal");

    if (!isLoaded) return null;

    return <UIModal {...props} ref={ref} />;
});

Modal.displayName = "Modal";
